import type {
  AddToCalendarDelegate,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from '@/features/shell/platform/types'
import {
  ExitAction,
  AuthenticatedEvent,
  SessionExpiredEvent,
} from '@/features/shell/platform/messages'

class DefaultExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(): ExternalUrlOpenHandle {
    const newWindow = window.open('about:blank', '_blank')
    return {
      open(url: string) {
        if (!newWindow) {
          return
        }

        newWindow.location.href = url
      },
      abort() {
        newWindow?.close()
      },
    }
  }
}

class DefaultDocumentDownloadDelegate implements DocumentDownloadDelegate {
  /**
   * On the web we require the use of form submission.
   */
  readonly requiresFormSubmission = true

  /** @inheritDoc */
  download() {
    // Not used due to using form submission
  }
}

class DefaultAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    // Create a URL for the file
    const url = URL.createObjectURL(icsFile)

    // Create a temporary link element
    const link = document.createElement('a')
    link.href = url
    link.download = 'calendar.ics'

    // Click it bro!
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

class DefaultLifecycleDelegate implements LifecycleDelegate {
  get supportsDelegatedExit() {
    // If we're running as an iframe, it's possible that consumers
    // are listening for the `close-iframe` action via the `message` event.
    return window.parent !== window.self
  }

  notifyAuthenticated(): void {
    window.parent.postMessage(AuthenticatedEvent(), '*')
  }

  notifySessionExpired(): void {
    window.parent.postMessage(SessionExpiredEvent(), '*')
  }

  requestExit(returnUrl: string): void {
    window.parent.postMessage(ExitAction(), '*')
    // Give the consumer (if any) enough time to handle the message,
    // but redirect after a small delay in case they don't to be safe,
    // or if the consumer isn't listening.
    setTimeout(() => {
      window.location.href = returnUrl
    }, 10)
  }
}

/**
 * Default delegate for the web.
 */
export class DefaultPlatformDelegate implements PlatformDelegate {
  private readonly _externalUrlDelegate = new DefaultExternalUrlDelegate()
  private readonly _documentDownloadDelegate =
    new DefaultDocumentDownloadDelegate()
  private readonly _addToCalendarDelegate = new DefaultAddToCalendarDelegate()
  private readonly _lifecycleDelegate = new DefaultLifecycleDelegate()

  constructor() {
    this.getExternalUrlDelegate = this.getExternalUrlDelegate.bind(this)
    this.getDocumentDownloadDelegate =
      this.getDocumentDownloadDelegate.bind(this)
    this.getAddToCalendarDelegate = this.getAddToCalendarDelegate.bind(this)
    this.getLifecycleDelegate = this.getLifecycleDelegate.bind(this)
  }

  getExternalUrlDelegate() {
    return this._externalUrlDelegate
  }

  getDocumentDownloadDelegate() {
    return this._documentDownloadDelegate
  }

  getAddToCalendarDelegate() {
    return this._addToCalendarDelegate
  }

  getLifecycleDelegate() {
    return this._lifecycleDelegate
  }
}
